import AddIcon from "@mui/icons-material/Add";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import Field from "components/Field";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import { Guid } from "guid-typescript";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  AdminPartner,
  ListRequestOfOrganizationIdFilter,
  ListResponseOfAdminPartner,
  OrganizationDetails,
  OrganizationIdFilter,
  PartnerSaveRequest,
  PartnerType,
  ResponseCode,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";

type OrganizationPartnersProps = {
  organization: OrganizationDetails;
};

type OrganizationPartnerFormValues = {
  id: string | null;
  name: string;
  orderType: string;
  companyCode: string;
  sendExternalMessages: boolean;
  type: string;
};

const useStyles = (theme: Theme) => ({
  partnersHeader: {
    display: "flex",
    justifyContent: "end",
    margin: theme.spacing(4, 0, 3, 0),
    width: "100%",
  },
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

const OrganizationPartners = ({ organization }: OrganizationPartnersProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { partnerService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "copiedToClipboardMessage" }),
          Severity.Success,
        ),
      );
    } catch (error) {
      console.error(error);
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "copyToClipboardFailed" }),
          Severity.Error,
        ),
      );
    }
  };

  const partners = useStrictQuery<ListResponseOfAdminPartner>(
    ["partners", organization.id, orderBy, order],
    () =>
      partnerService.getPartners(
        ListRequestOfOrganizationIdFilter.fromJS({
          pageSize: -1,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: OrganizationIdFilter.fromJS({
            organizationId: organization.id,
          }),
        }),
      ),
    {
      keepPreviousData: true,
    },
  );

  const partnersWithId =
    partners.data?.data?.map((x) => ({ ...x, id: x.id! })) ?? [];

  const [createOpen, createOpenActions] = useBoolean(false);

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "orderType",
      align: "left",
      type: "text",
      label: t({ id: "orderTypeLabel" }),
    },
    {
      accessor: "companyCode",
      align: "left",
      type: "text",
      label: t({ id: "companyLabel" }),
    },
    {
      accessor: "sendExternalMessages",
      align: "center",
      type: "boolean",
      label: t({ id: "sendExternalMessagesLabel" }),
    },
    {
      accessor: "type",
      align: "left",
      type: "node",
      View: ({ value }: { value: AdminPartner }) => (
        <>
          {value.type === PartnerType.External
            ? t({ id: "partnerTypeExternal" })
            : t({ id: "partnerTypeInternal" })}
        </>
      ),
      label: t({ id: "typeLabel" }),
    },
    {
      accessor: "id",
      align: "left",
      width: 0,
      type: "node",
      noSort: true,
      View: ({ value }: { value: AdminPartner }) => (
        <IconButton
          size="small"
          onClick={async () =>
            value.secret && (await copyToClipboard(value.id!))
          }
        >
          <Tooltip title={value.id!}>
            <FileCopyIcon />
          </Tooltip>
        </IconButton>
      ),
      label: t({ id: "partnerIdLabel" }),
    },
    {
      accessor: "secret",
      align: "left",
      width: 0,
      type: "node",
      noSort: true,
      View: ({ value }: { value: AdminPartner }) => (
        <IconButton
          size="small"
          onClick={async () =>
            value.secret && (await copyToClipboard(value.secret))
          }
        >
          <FileCopyIcon />
        </IconButton>
      ),
      label: t({ id: "secretLabel" }),
    },
  ];

  const savePartner = async (values: OrganizationPartnerFormValues) => {
    try {
      const result = await partnerService.savePartner(
        PartnerSaveRequest.fromJS({
          ...values,
          organizationId: organization.id,
        }),
      );

      if (result.responseCode === ResponseCode.OK) {
        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "partnerSaveSuccess" })),
        );
        queryClient.invalidateQueries(["partners", organization.id]);
        createOpenActions.setFalse();
      }
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const validateForm = (values: OrganizationPartnerFormValues) => {
    const errors = {} as any;

    if (values.id && !Guid.isGuid(values.id)) {
      errors.id = t({ id: "invalidGuid" });
    }

    return errors;
  };

  return (
    <Box>
      <LazyTable
        {...{
          rowCount: partners.data?.totalRecords ?? 0,
          rows: partnersWithId,
          loading: partners.isFetching,
          titleComponent: (
            <Box sx={styles.partnersHeader}>
              <Button
                onClick={createOpenActions.setTrue}
                startIcon={<AddIcon />}
              >
                <T id="addPartner" />
              </Button>
            </Box>
          ),
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <Form
          validate={validateForm}
          onSubmit={savePartner}
          render={({ handleSubmit, submitting, submitError }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="id">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "partnerIdLabel" })}
                          InputLabelProps={{ required: false }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="name" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "nameLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="orderType" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "orderTypeLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="companyCode" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "companyCodeLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="sendExternalMessages" type="checkbox">
                    {({ input }) => (
                      <Grid item xs={12}>
                        <FormLabel>
                          <T id="sendExternalMessagesLabel" />
                        </FormLabel>
                        <Checkbox {...input} />
                      </Grid>
                    )}
                  </Field>

                  <Field name="type" initialValue={PartnerType.Internal}>
                    {({ input }) => (
                      <Grid item xs={12}>
                        <FormControl variant="standard" fullWidth>
                          <InputLabel>{t({ id: "typeLabel" })}</InputLabel>
                          <Select {...input}>
                            <MenuItem value={PartnerType.Internal}>
                              {t({ id: "partnerTypeInternal" })}
                            </MenuItem>
                            <MenuItem value={PartnerType.External}>
                              {t({ id: "partnerTypeExternal" })}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Field>
                </Grid>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </DialogContent>

              <DialogActions>
                <Button
                  disabled={submitting}
                  onClick={() => {
                    createOpenActions.setFalse();
                  }}
                >
                  {t({ id: "cancelButton" })}
                </Button>

                <Button disabled={submitting} type="submit">
                  {t({ id: "saveButton" })}
                  {submitting && (
                    <CircularProgress
                      style={{ marginLeft: "10px" }}
                      color="inherit"
                      size={14}
                    />
                  )}
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default withErrorBoundary(OrganizationPartners);
